import { DataGeneratorSimpleRelationship } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { DataGeneratorSimpleRelationshipValue } from 'libs/end-to-end/src/data-generator/data-generator-input';

// ---------------------------------------------------------
// Mapping from discrete relationship labels to numeric strength
// ---------------------------------------------------------

const SIMPLE_RELATIONSHIP_VALUE_TO_SLIDER: Record<
  DataGeneratorSimpleRelationshipValue,
  number
> = {
  strongNegative: -1.0,
  moderateNegative: -0.5,
  weakNegative: -0.25,
  neutral: 0.0,
  weakPositive: 0.25,
  moderatePositive: 0.5,
  strongPositive: 1.0,
};

function parseStrengthLabel(
  label: DataGeneratorSimpleRelationshipValue,
): number {
  const value = SIMPLE_RELATIONSHIP_VALUE_TO_SLIDER[label];
  if (value === undefined) {
    throw new Error(`Unknown relationship strength label: ${label}`);
  }
  return value;
}

// ---------------------------------------------------------
// Matrix operations utilities
// ---------------------------------------------------------

function matrixMultiply(a: number[][], b: number[][]): number[][] {
  const n = a.length;
  const result: number[][] = Array(n)
    .fill(0)
    .map(() => Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      let sum = 0;
      for (let k = 0; k < n; k++) {
        sum += a[i][k] * b[k][j];
      }
      result[i][j] = sum;
    }
  }

  return result;
}

function matrixAdd(a: number[][], b: number[][], scale = 1): number[][] {
  const n = a.length;
  const result: number[][] = Array(n)
    .fill(0)
    .map(() => Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      result[i][j] = a[i][j] + b[i][j] * scale;
    }
  }

  return result;
}

function copyMatrix(matrix: number[][]): number[][] {
  return matrix.map((row) => [...row]);
}

function clampMatrix(matrix: number[][], min: number, max: number): number[][] {
  return matrix.map((row) =>
    row.map((val) => Math.max(min, Math.min(max, val))),
  );
}

function setDiagonal(matrix: number[][], value: number): void {
  for (let i = 0; i < matrix.length; i++) {
    matrix[i][i] = value;
  }
}

// ---------------------------------------------------------
// Core helpers for building and propagating relationships
// ---------------------------------------------------------

function buildColumnIndex(columnNames: string[]): Record<string, number> {
  const index: Record<string, number> = {};
  columnNames.forEach((name, i) => {
    index[name] = i;
  });
  return index;
}

function buildDirectWeightMatrix(
  relationships: DataGeneratorSimpleRelationship[],
  colIndex: Record<string, number>,
): number[][] {
  const n = Object.keys(colIndex).length;
  const W: number[][] = Array(n)
    .fill(0)
    .map(() => Array(n).fill(0));

  for (const rel of relationships) {
    const a = rel.columnA;
    const b = rel.columnB;
    const strengthLabel = rel.value;
    const w = parseStrengthLabel(strengthLabel);

    const i = colIndex[a];
    const j = colIndex[b];

    // Symmetric for heatmap
    W[i][j] = w;
    W[j][i] = w;
  }

  return W;
}

function computeImpliedMatrix(
  W: number[][],
  maxOrder = 3,
  coeffs: number[] = [1.0, 0.5, 0.25],
): number[][] {
  const n = W.length;

  if (coeffs.length < maxOrder) {
    throw new Error('coeffs length must be >= maxOrder');
  }

  // Start with zeros and accumulate
  const implied: number[][] = Array(n)
    .fill(0)
    .map(() => Array(n).fill(0));

  // Current power of W
  let M = copyMatrix(W);

  for (let order = 1; order <= maxOrder; order++) {
    const c = coeffs[order - 1];
    // Add c * M to implied
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        implied[i][j] += c * M[i][j];
      }
    }
    // Next power
    M = matrixMultiply(M, W);
  }

  // Clamp to [-1, 1]
  const clamped = clampMatrix(implied, -1.0, 1.0);

  // Set diagonal to 1 (perfect self-association)
  setDiagonal(clamped, 1.0);

  return clamped;
}

// ---------------------------------------------------------
// Public API for the heatmap backend
// ---------------------------------------------------------

export interface ColorMatrixMetadata {
  directWeights: number[][];
}

export interface ColorMatrixOutputType {
  columns: string[];
  matrix: number[][];
  metadata: ColorMatrixMetadata;
}

/**
 * Compute implied correlation heatmap from data generator configuration.
 *
 * Given column names and relationships, returns a structure containing:
 * - columns: list of column names
 * - matrix: 2D array of implied strengths (NxN), where matrix[i][j] ∈ [-1, 1]
 * - metadata: includes direct weights
 */
export const getColorMatrix = (
  columns: string[],
  relationships: DataGeneratorSimpleRelationship[],
): ColorMatrixOutputType => {
  // 1. Build column index
  const colIndex = buildColumnIndex(columns);

  // 2. Build direct weight matrix from user relationships
  const WDirect = buildDirectWeightMatrix(relationships, colIndex);

  // 3. Compute implied matrix
  const WImplied = computeImpliedMatrix(WDirect);

  return {
    columns,
    matrix: WImplied,
    metadata: {
      directWeights: WDirect,
    },
  };
};
